import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import React from 'react';
import { useDrag } from 'react-dnd';
import type { CardType, PlotCard } from '../types';
import { cardTypeColors } from '../types';
import EnhancedContextMenu from './enhanced-context-menu';

// Define the item type for drag and drop
const ITEM_TYPE = 'PLOT_CARD';


// Import the theme-specific styles
import '@/styles/plotcard-themes.css';

// Helper function to get card styling safely
const getCardStyle = (type?: CardType) => {
    // Return the styling for the specified type if it exists, otherwise default to 'note'
    return cardTypeColors[type && type in cardTypeColors ? type : 'note'];
};



interface DraggablePlotCardProps {
    card: PlotCard;
    isSelected: boolean;
    compactMode: boolean;
    index?: number;
    onSelect: (card: PlotCard, isMultiSelect?: boolean) => void;
    onMoveCard: (cardId: number, newPlotLineId: number, newChapterId: number, newPosition?: number) => void;
    onEdit?: (card: PlotCard) => void;
    onDelete?: (card: PlotCard) => void;
    onCopy?: (card: PlotCard) => void;
    onShowDetails?: (card: PlotCard) => void;
}

export const DraggablePlotCard: React.FC<DraggablePlotCardProps> = ({
    card,
    isSelected,
    compactMode,
    index,

    onEdit,
    onDelete,
    onCopy,
    onShowDetails
}) => {
    // Set up drag
    const [{ isDragging }, drag] = useDrag({
        type: ITEM_TYPE,
        item: () => {
            console.log('Starting drag for card:', card.id, 'chapter:', card.chapter_id || card.y, 'position:', card.position);
            return {
                id: card.id,
                x: card.x,
                y: card.chapter_id || card.y, // Ensure we use chapter_id if available
                chapter_id: card.chapter_id, // Include chapter_id explicitly
                position: card.position !== null && card.position !== undefined ? card.position : (index || 0),
                title: card.title // Include title for better logging
            };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: (_item, monitor) => {
            const didDrop = monitor.didDrop();
            if (!didDrop) {
                console.log('Card was not dropped in a valid drop target, returning to original position');
            }
        }
    });

    // Handle card click - show details instead of selecting
    const handleCardClick = (_e: React.MouseEvent) => {
        // Prevent click when dragging
        if (isDragging) return;

        // If showDetails handler exists, use it to show details
        if (onShowDetails) {
            onShowDetails(card);
        }
        // Fallback to edit handler if showDetails doesn't exist
        else if (onEdit) {
            onEdit(card);
        }
    };

    // Handle edit
    const handleEdit = (card: PlotCard) => {
        if (onEdit) onEdit(card);
    };

    // Handle delete
    const handleDelete = (card: PlotCard) => {
        if (onDelete) onDelete(card);
    };

    const cardContent = (
        <div
            ref={drag}
            data-plotcard-type={card.type || 'note'}
            className={cn(
                "p-3 rounded-xl border shadow-sm transition-all duration-200",
                getCardStyle(card.type).bg,
                getCardStyle(card.type).border,
                getCardStyle(card.type).hover,
                isSelected && "ring-1 ring-primary ring-offset-1 ring-offset-[#111315]",
                "hover:shadow-md cursor-grab active:cursor-grabbing group",
                isDragging && "opacity-50 grayscale"
            )}
            onClick={handleCardClick}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className={cn("font-medium text-sm truncate group-hover:text-primary transition-colors")}>
                        {card.title}
                    </div>
                    {!compactMode && card.content && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">
                            {card.content}
                        </div>
                    )}
                </div>
                <Badge
                    variant="tag"
                    className={cn(
                        "shrink-0 opacity-70 group-hover:opacity-100 text-[10px] px-1.5 py-0 font-medium uppercase tracking-wider backdrop-blur-sm bg-background/50",
                        getCardStyle(card.type).text
                    )}
                >
                    {card.type || 'note'}
                </Badge>
            </div>
        </div>
    );

    // If we don't have edit or delete handlers, just return the card
    if (!onEdit && !onDelete) {
        return cardContent;
    }

    // Otherwise, wrap it in the enhanced context menu
    return (
        <EnhancedContextMenu
            currentCard={card}
            onEditCard={handleEdit}
            onDeleteCard={handleDelete}
            onCopyCard={onCopy}
            selectedItems={[]}
        >
            {cardContent}
        </EnhancedContextMenu>
    );
};